import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { tenantPermissionResourceFilters } from './tenant-permission-resource.filters';
import {
  NEW_TENANT_PERMISSION_RESOURCE,
  TENANT_PERMISSION_RESOURCE_PAGE_SIZES,
  TENANT_PERMISSION_RESOURCE_PATH,
  tenantPermissionResourcePath,
} from './tenant-permission-resource.search';

// Manifestos de Permissões por Correspondente (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K;
// o fio da exclusão vem por `import()` na hora de rodar. Módulo Segurança: `agent: false` (U24), então
// nenhuma ação vira ferramenta nem a tela entrega contexto.

export { TENANT_PERMISSION_RESOURCE_PATH };

/** O handle da rota (`router.tsx:2034-2037`); o grupo `/seguranca` pede também `User` ou `UserGroup`. */
export const TENANT_PERMISSION_RESOURCE_READ = can('TenantRequest', 'Read');

const idInput = z.object({ id: z.string().min(1).max(64) });
const loadApi = () => import('./tenant-permission-resource.api');
const loadForm = () => import('./tenant-permission-resource.form');
const needsScreen = () => {
  throw new Error('This action runs only from the open tenant permission resource form.');
};

/** "Novo Cadastro" só com `TenantRequest:Create` (`TenantPermissionResourceHeader.tsx:21`, `:49`). */
export const tenantPermissionResourceCreate = action({
  id: 'tenantPermissionResource.create',
  kind: 'draft',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco que escolhe quais recursos um correspondente vê na tela de permissões. Nada é gravado até a pessoa salvar.`,
  keywords: [msg`recursos`, msg`correspondente`, msg`permissões`],
  permission: can('TenantRequest', 'Create'),
  run: ({ nav }) => nav.to(tenantPermissionResourcePath(NEW_TENANT_PERMISSION_RESOURCE)),
});

/** Lápis e clique na linha, sem gate próprio (`TenantPermissionResourceList.tsx:98-102`, `:141`). */
export const tenantPermissionResourceOpen = action({
  id: 'tenantPermissionResource.open',
  kind: 'navigate',
  row: true,
  label: msg`Editar`,
  description: msg`Abre o cadastro de recursos de um correspondente da lista. Nada muda só por abrir.`,
  input: idInput,
  run: ({ nav, input }) => nav.to(tenantPermissionResourcePath(input.id)),
});

/** Lixeira só com `TenantRequest:Delete` (`TenantPermissionResourceList.tsx:103`); textos do popup (`ListContainer.tsx:91-119`). */
export const tenantPermissionResourceDelete = action({
  id: 'tenantPermissionResource.delete',
  kind: 'destructive',
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui o cadastro de recursos de um correspondente, que volta a ver todos os recursos na tela de permissões. Pede a confirmação da pessoa.`,
  permission: can('TenantRequest', 'Delete'),
  input: idInput.extend({ name: z.string().max(200).optional() }),
  // Step-up novo (`tenantPermissionResourceContext.tsx:315-321` do legado): preso ao cadastro.
  stepUp: { action: 'TenantPermissionResourceWrite', resourceId: (input) => input.id },
  preview: ({ name }) => ({
    title: name ? i18n._(msg`Excluir ${name}`) : i18n._(msg`Excluir`),
    detail: i18n._(
      msg`Tem certeza que deseja excluir este cadastro? O correspondente voltará a ver todos os recursos na tela de permissões.`,
    ),
    tone: 'destructive',
  }),
  run: async ({ input, stepUp }) => {
    const [api, form] = await Promise.all([loadApi(), loadForm()]);
    try {
      await api.deleteTenantPermissionResource(input.id, stepUp);
    } catch (error) {
      const apiError = api.normalizeTenantPermissionResourceError(error);
      throw form.asLegacyToastError(apiError, apiError.mapped.errorMessage);
    }
    return input.id;
  },
});

export const tenantPermissionResourceFilter = filterAction(
  'tenantPermissionResource.filter',
  tenantPermissionResourceFilters,
  { description: msg`Filtra a lista pelo nome do correspondente. Filtro omitido é limpo.` },
);

export const tenantPermissionResourceListPage = listPageAction('tenantPermissionResource.list.page');

export const tenantPermissionResourceListScreen = defineScreen({
  id: 'tenantPermissionResource.list',
  path: TENANT_PERMISSION_RESOURCE_PATH,
  title: msg`Permissões por Correspondente`,
  description: msg`Segurança: quais recursos cada correspondente vê na tela de permissões.`,
  permission: TENANT_PERMISSION_RESOURCE_READ,
  access: { rootTenancy: true },
  agent: false,
  place: { icon: 'shield-check', section: 'security' },
  actions: [
    tenantPermissionResourceCreate,
    tenantPermissionResourceFilter,
    tenantPermissionResourceListPage,
    tenantPermissionResourceOpen,
    tenantPermissionResourceDelete,
  ],
});

/** "Atualizar" do formulário, só na edição (`TenantPermissionResourceForm.tsx:78-91`). */
export const tenantPermissionResourceRefresh = action({
  id: 'tenantPermissionResource.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o cadastro de recursos aberto. Não muda nada.`,
  input: z.strictObject({}),
  run: needsScreen,
});

/** "Salvar" (`TenantPermissionResourceForm.tsx:92-98`): `Create` no novo, `Update` na edição. */
export const tenantPermissionResourceSave = action({
  id: 'tenantPermissionResource.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Salva os recursos que o correspondente vê na tela de permissões, no cadastro novo ou na edição. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({
    title: i18n._(msg`Salvar cadastro de recursos`),
    detail: i18n._(msg`O correspondente passa a ver na tela de permissões só os recursos marcados.`),
  }),
  run: needsScreen,
});

export const tenantPermissionResourceFormScreen = defineScreen({
  id: 'tenantPermissionResource.form',
  path: `${TENANT_PERMISSION_RESOURCE_PATH}/$id`,
  title: msg`Cadastro de recursos`,
  description: msg`Segurança: cria ou edita a lista de recursos que um correspondente vê na tela de permissões.`,
  permission: TENANT_PERMISSION_RESOURCE_READ,
  access: { rootTenancy: true },
  agent: false,
  place: {
    icon: 'shield-check',
    parent: 'tenantPermissionResource.list',
    record: msg`Cadastro de recursos`,
  },
  actions: [tenantPermissionResourceRefresh, tenantPermissionResourceSave],
});

export { TENANT_PERMISSION_RESOURCE_PAGE_SIZES };
